
import React, { useEffect, useState } from "react";
import "../Styles/Auctions.css"

const emptyForm = {
  url: "",
  title: "",
  description: "",
  imageUrl: "",
  published: false,
  featured: false,
  ended: false,
};

export default function AuctionsTab() {
  const [auctions, setAuctions] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  async function loadAuctions() {
    try {
      const response = await fetch("/api/admin/auctions", {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Could not load auctions.");
      }

      setAuctions(await response.json());
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => {
    loadAuctions();
  }, []);

  async function fetchDetails() {
    if (!form.url.trim()) {
      setError("Paste an AirAuctioneer link first.");
      return;
    }

    setPreviewing(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch("/api/admin/auctions/preview", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url: form.url }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Could not fetch auction details.");
      }

      setForm((current) => ({
        ...current,
        url: data.url || current.url,
        title: data.title || current.title,
        description: data.description || current.description,
        imageUrl: data.imageUrl || current.imageUrl,
      }));

      setMessage(
        data.title || data.imageUrl
          ? "Details retrieved. Please check them before saving."
          : "No usable metadata was found. Enter the details manually."
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  }

  async function saveAuction(event) {
    event.preventDefault();

    setBusy(true);
    setError("");
    setMessage("");

    try {
      const endpoint = editingId
        ? `/api/admin/auctions/${editingId}`
        : "/api/admin/auctions";

      const response = await fetch(endpoint, {
        method: editingId ? "PUT" : "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Could not save auction.");
      }

      setMessage(
        editingId
          ? "Auction updated successfully."
          : "Auction added successfully."
      );

      setForm(emptyForm);
      setEditingId(null);
      await loadAuctions();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  function editAuction(auction) {
    setEditingId(auction._id);

    setForm({
      url: auction.url || "",
      title: auction.title || "",
      description: auction.description || "",
      imageUrl: auction.imageUrl || "",
      published: Boolean(auction.published),
      featured: Boolean(auction.featured),
      ended: Boolean(auction.ended),
    });

    setMessage("");
    setError("");

    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setMessage("");
  }

  async function deleteAuction(auction) {
    if (!window.confirm(`Delete "${auction.title}"?`)) {
      return;
    }

    setError("");
    setMessage("");

    try {
      const response = await fetch(
        `/api/admin/auctions/${auction._id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Could not delete auction.");
      }

      if (editingId === auction._id) {
        cancelEdit();
      }

      setMessage("Auction deleted.");
      await loadAuctions();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section className="admin-auctions">
      <h2>Manage Auctions</h2>

      <p className="muted">
        Paste an AirAuctioneer item link, retrieve its details and
        publish it when ready.
      </p>

      {message && (
        <p className="auction-admin-success" role="status">
          {message}
        </p>
      )}

      {error && (
        <p className="auction-admin-error" role="alert">
          {error}
        </p>
      )}

      <form className="auction-admin-form" onSubmit={saveAuction}>
        <h3>{editingId ? "Edit Auction" : "Add Auction"}</h3>

        <label htmlFor="auction-url">
          AirAuctioneer item URL
        </label>

        <div className="auction-url-row">
          <input
            id="auction-url"
            type="url"
            required
            value={form.url}
            onChange={(event) => update("url", event.target.value)}
            placeholder="https://airauctioneer.com/..."
          />

          <button
            type="button"
            className="btn"
            disabled={previewing}
            onClick={fetchDetails}
          >
            {previewing ? "Fetching…" : "Fetch Details"}
          </button>
        </div>

        <label htmlFor="auction-title">Title</label>

        <input
          id="auction-title"
          required
          maxLength={200}
          value={form.title}
          onChange={(event) => update("title", event.target.value)}
          placeholder="e.g. 4 Ball at The Manor House Golf Club"
        />

        <label htmlFor="auction-description">
          Description
        </label>

        <textarea
          id="auction-description"
          rows={4}
          maxLength={2000}
          value={form.description}
          onChange={(event) =>
            update("description", event.target.value)
          }
          placeholder="Short description of the auction item"
        />

        <label htmlFor="auction-image">
          Image URL
        </label>

        <input
          id="auction-image"
          type="url"
          value={form.imageUrl}
          onChange={(event) =>
            update("imageUrl", event.target.value)
          }
          placeholder="https://..."
        />

        {form.imageUrl && (
          <img
            className="auction-admin-preview"
            src={form.imageUrl}
            alt="Auction preview"
          />
        )}

        <div className="auction-admin-options">
          <label>
            <input
              type="checkbox"
              checked={form.published}
              onChange={(event) =>
                update("published", event.target.checked)
              }
            />
            Published
          </label>

          <label>
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(event) =>
                update("featured", event.target.checked)
              }
            />
            Featured
          </label>

          <label>
            <input
              type="checkbox"
              checked={form.ended}
              onChange={(event) =>
                update("ended", event.target.checked)
              }
            />
            Auction ended
          </label>
        </div>

        <div className="auction-admin-buttons">
          <button
            type="submit"
            className="btn"
            disabled={busy}
          >
            {busy
              ? "Saving…"
              : editingId
                ? "Save Changes"
                : "Add Auction"}
          </button>

          {editingId && (
            <button
              type="button"
              className="btn"
              onClick={cancelEdit}
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <h3>Existing Auctions ({auctions.length})</h3>

      <div className="auction-admin-list">
        {auctions.map((auction) => (
          <article
            className="auction-admin-item"
            key={auction._id}
          >
            {auction.imageUrl && (
              <img
                src={auction.imageUrl}
                alt=""
                loading="lazy"
              />
            )}

            <div className="auction-admin-item-info">
              <h4>{auction.title}</h4>

              <p>
                {auction.published ? "Published" : "Draft"}
                {auction.featured ? " · Featured" : ""}
                {auction.ended ? " · Ended" : ""}
              </p>

              <a
                href={auction.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open on AirAuctioneer ↗
              </a>
            </div>

            <div className="auction-admin-item-actions">
              <button
                type="button"
                className="btn"
                onClick={() => editAuction(auction)}
              >
                Edit
              </button>

              <button
                type="button"
                className="btn danger"
                onClick={() => deleteAuction(auction)}
              >
                Delete
              </button>
            </div>
          </article>
        ))}

        {auctions.length === 0 && (
          <p className="muted">No auctions have been added yet.</p>
        )}
      </div>
    </section>
  );
}